'use client';

import { Fragment } from 'react';
import type { MouseEvent } from 'react';

import type { GetResponseType } from '@/api/types/api';
import type { ApiResponseDefinition } from '@/api/types/api-definition';
import Image from '@/components/Image';
import { useCustomerService } from '@/hooks/useCustomerService';
import { useTranslations } from '@/i18n/use-translations';
import { IS_DE, IS_FR, IS_US } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { setSession } from '@/utils/tools';
import {
  FacebookOutlined,
  HelpEmailOutlined,
  HelpOnlineOutlined,
  HelpPhoneOutlined,
  InstagramOutlined,
  PinterestOutlined,
  TiktokOutlined,
  TwitterOutlined,
  YoutubeOutlined,
} from '@zeelool/icon';
import { useLocale } from 'next-intl';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { shouldShowUSFooterSubscribe } from '../../subscribeVisibility';
import CountrySiteList2026 from './CountrySiteList';
import Subscribe2026 from './Subscribe';
import SubscribeCoupon2026 from './SubscribeCoupon';

interface FooterNavLink {
  href: string;
  title: string;
}

interface FooterNavContent {
  describe?: string;
  layout_title?: string;
  list?: FooterNavLink[];
}

interface Footer2026Props {
  navList: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  SDSocialMediaClick: (btn: string) => void;
  SDBottomNavTabClick: (
    first_tab_name: string,
    second_tab_name?: string,
    first_tab_index?: number,
    second_tab_index?: number,
  ) => void;
  isShowSubscribe: boolean;
  deviceType: EPlatform;
}

interface LegalLink {
  href: string;
  label: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

const PAYMENT_METHOD_OFFSETS = [-20, -90, -160, -230, -300, -370, -440, -510] as const;
const PAYMENT_SPRITE_SCALE = 0.72;

export default function Footer2026({
  navList,
  SDSocialMediaClick,
  SDBottomNavTabClick,
  isShowSubscribe,
  deviceType,
}: Footer2026Props) {
  const bottomSubscribeBoxTxt =
    (navList[0]?.content as FooterNavContent | undefined)?.describe || '';
  const pathname = usePathname();
  const t = useTranslations('layout.footer');
  const locale = useLocale();
  const router = useRouter();
  const isShowCouponSubscribe = !IS_US && isShowSubscribe;
  const isShowSubscribeUS = shouldShowUSFooterSubscribe(pathname);
  const paymentMethodsPicture = IS_FR
    ? '/zeelool-next/home-page/footer_accept_fr.png'
    : '/zeelool-next/home-page/footer_accept.png';
  const paymentMethodOffsets = [
    ...PAYMENT_METHOD_OFFSETS,
    ...(env.NEXT_PUBLIC_SHOW_KLARNA === 'true' ? [-580] : []),
    ...(env.NEXT_PUBLIC_SHOW_AFTER_PAY === 'true' ? [-650] : []),
  ];

  const { openCustomerService } = useCustomerService({
    inAppH5: false,
  });

  const legalLinks: LegalLink[] = [
    { href: '/zeelool-promise', label: t('zeeloolPromise') },
    { href: '/return-refund', label: t('shippingReturns') },
    ...(IS_DE ? [{ href: '/impressum', label: 'IMPRESSUM' }] : []),
    {
      href: locale === 'de' ? '/terms-conditions' : '/privacy-security',
      label: t('privacySecurity'),
    },
    {
      href: locale === 'de' ? '/agb' : '/terms-conditions',
      label: t('termsConditions'),
    },
    ...(env.NEXT_PUBLIC_SHOW_KLARNA === 'true' ? [{ href: '/klarna', label: t('klarna') }] : []),
    {
      href: env.NEXT_PUBLIC_COOKIE_TARGET,
      label: t('doNotSellMyPersonalInformation'),
      onClick: (event) => {
        if (env.NEXT_PUBLIC_ENABLE_ONETRUST === 'true') {
          event.preventDefault();
          window?.OneTrust?.ToggleInfoDisplay();
        }
      },
    },
  ];

  return (
    <footer
      className={`bg-black text-white ${pathname.includes('/goods-detail/') ? 'pb-[80px]' : ''}`}
    >
      {isShowSubscribeUS ? <Subscribe2026 /> : null}
      {isShowCouponSubscribe ? (
        <SubscribeCoupon2026 bottomSubscribeBoxTxt={bottomSubscribeBoxTxt} />
      ) : null}

      <div className="flex w-full items-start justify-between border-y border-[#6c6c6c] px-[72px] py-[24px]">
        <div className="flex shrink-0 flex-col items-start gap-[32px]">
          <b className="font-bb text-[48px] leading-[58px] text-white">{t('needHelp')}</b>

          <dl className="flex w-full flex-col gap-[20px]">
            <div className="flex min-h-[32px] items-center gap-[8px]">
              <dt className="flex size-[32px] shrink-0 items-center justify-center">
                <HelpOnlineOutlined className="text-[20px] text-white" />
              </dt>
              <dd className="text-[14px] leading-[17px]">
                {env.NEXT_PUBLIC_FULL_SITE_NAME === 'ZEELOOL FR' ? (
                  <button
                    type="button"
                    className="cursor-pointer border-0 bg-transparent p-0 font-bb text-white"
                    aria-label={t('liveChat')}
                    onClick={openCustomerService}
                  >
                    {t('liveChat')}
                  </button>
                ) : (
                  <Link
                    prefetch={false}
                    className="font-bb text-white"
                    aria-label={t('contactUs')}
                    href="/contact-zeelool"
                    onClick={(event) => {
                      event.preventDefault();
                      SDBottomNavTabClick('Need Help?', 'Contact Us', 0, 0);
                      router.push('/contact-zeelool');
                    }}
                  >
                    {t('contactUs')}
                  </Link>
                )}
              </dd>
            </div>

            <div className="flex min-h-[32px] items-center gap-[8px]">
              <dt className="flex size-[32px] shrink-0 items-center justify-center">
                <HelpEmailOutlined className="text-[24px] text-white" />
              </dt>
              <dd className="flex flex-col gap-[4px] text-[14px] leading-[17px]">
                {env.NEXT_PUBLIC_FULL_SITE_NAME === 'ZEELOOL FR' ? (
                  <Link
                    prefetch={false}
                    className="font-bb text-white"
                    aria-label={t('contactUs')}
                    href="/contact-zeelool"
                    onClick={(event) => {
                      event.preventDefault();
                      SDBottomNavTabClick('Need Help?', 'Contact Us', 0, 0);
                      router.push('/contact-zeelool');
                    }}
                  >
                    {t('ContactUsViaForm')}
                  </Link>
                ) : (
                  <span className="font-bb">{t('businessCooperation')}</span>
                )}
                <Link
                  prefetch={false}
                  className="font-br text-white"
                  href={`mailto:${env.NEXT_PUBLIC_MARKET_EMAIL}`}
                >
                  {env.NEXT_PUBLIC_MARKET_EMAIL}
                </Link>
              </dd>
            </div>

            {env.NEXT_PUBLIC_FOOTER_SHOW_TEL === 'true' ? (
              <div className="flex min-h-[32px] items-center gap-[8px]">
                <dt className="flex size-[32px] shrink-0 items-center justify-center">
                  <HelpPhoneOutlined className="text-[20px] text-white" />
                </dt>
                <dd className="flex flex-col gap-[4px] font-br text-white">
                  <span className="text-[14px] leading-[17px]">{t('callInfo')}</span>
                  <span className="text-[12px] leading-[14px]">
                    {env.NEXT_PUBLIC_CUSTOMER_SERVICE_TIME}
                  </span>
                </dd>
              </div>
            ) : null}
          </dl>

          <Link
            prefetch={false}
            href="/track-order"
            aria-label={t('trackOrder')}
            className="flex h-[40px] w-[152px] items-center justify-center rounded-[20px] bg-primary-purple font-bb text-[14px] leading-[17px] text-white"
            onClick={(event) => {
              event.preventDefault();
              SDBottomNavTabClick('Need Help?', 'Track Order', 0, 0);
              router.push('/track-order');
            }}
          >
            {t('trackOrder')}
          </Link>

          <div className="flex flex-col gap-[12px]">
            <p className="font-br text-[14px] leading-[17px]">{t('followUs')}</p>
            <div className="flex items-center gap-[24px]">
              {env.NEXT_PUBLIC_FB ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_FB}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('fb')}
                >
                  <FacebookOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_YTB ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_YTB}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('youtube')}
                >
                  <YoutubeOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_INS ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_INS}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('ins')}
                >
                  <InstagramOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_X ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_X}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('twitter')}
                >
                  <TwitterOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_PIN ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_PIN}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('pinterest')}
                >
                  <PinterestOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_TT ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_TT}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('tiktok')}
                >
                  <TiktokOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
            </div>
            {env.NEXT_PUBLIC_WEB_SHOW_FB_GROUP === 'true' ? (
              <Link
                prefetch={false}
                href={env.NEXT_PUBLIC_FB_GROUP}
                aria-label={t('social.joinGroup')}
                target="_blank"
                rel="noopener nofollow noreferrer"
              >
                <Image
                  src={env.NEXT_PUBLIC_FB_GROUP_IMG}
                  alt={t('social.joinGroup')}
                  width={204}
                  height={48}
                />
              </Link>
            ) : null}
          </div>

          <CountrySiteList2026
            onCountryClick={(shortName) => SDBottomNavTabClick(`Region_${shortName}`)}
            deviceType={deviceType}
          />
        </div>

        <nav
          className="flex min-w-0 flex-1 flex-wrap items-start justify-end gap-[40px]"
          aria-label="Footer"
        >
          {navList.map((item, index) => {
            const content = item.content as FooterNavContent;

            if (!content.list) {
              return null;
            }

            return (
              <div key={item.id} className="w-[220px] shrink-0">
                <p className="mb-[16px] font-bb text-[16px] leading-[19px] text-white">
                  {content.layout_title}
                </p>
                <div className="flex flex-col items-start gap-[12px]">
                  {content.list.map((navItem, navItemIndex) => (
                    <Link
                      prefetch={false}
                      href={navItem.href}
                      className="font-br text-[14px] leading-[17px] text-[#999999] hover:text-white"
                      key={`${navItem.title}-${navItemIndex}`}
                      onClick={(event) => {
                        event.preventDefault();
                        SDBottomNavTabClick(
                          content.layout_title || '',
                          navItem.title,
                          index,
                          navItemIndex,
                        );
                        if (navItem.href?.includes('goods-list')) {
                          setSession('zl_goodsListEnterPageScene', '全局导航');
                        }
                        if (navItem.href === env.NEXT_PUBLIC_COOKIE_TARGET) {
                          window.OneTrust.ToggleInfoDisplay();
                        } else if (
                          (navItem.href.includes('zeelool.com') ||
                            navItem.href.includes('xmslol.cn')) &&
                          !navItem.href.includes(env.NEXT_PUBLIC_HELP_CENTER_PATH)
                        ) {
                          const href = new URL(navItem.href);
                          const url = href.href.replace(href.origin, '');
                          router.push(url);
                        } else {
                          window.location.href = navItem.href;
                        }
                      }}
                    >
                      {navItem.title}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      <div className="flex w-full flex-col items-center pt-[24px]">
        <ul className="flex w-full flex-wrap items-center justify-center gap-x-[32px] gap-y-[16px] px-[160px] pb-[12px] pt-[24px] font-br text-[14px] uppercase leading-[17px] text-white xl:flex-nowrap">
          {legalLinks.map((legalLink, index) => (
            <Fragment key={legalLink.href}>
              {index > 0 ? <li aria-hidden="true">|</li> : null}
              <li className="whitespace-nowrap text-center">
                <Link
                  prefetch={false}
                  href={legalLink.href}
                  aria-label={legalLink.label}
                  className="text-white"
                  onClick={legalLink.onClick}
                >
                  {legalLink.label}
                </Link>
              </li>
            </Fragment>
          ))}
        </ul>

        <div className="flex w-full items-center justify-center px-[160px] py-[12px]">
          <div className="flex items-center gap-[12px]">
            <p className="whitespace-nowrap font-bb text-[14px] leading-[17px] text-white">
              {t('payments.weAccept')}
            </p>
            <div className="flex items-center gap-[8px]" aria-hidden="true">
              {paymentMethodOffsets.map((offset) => (
                <span
                  key={offset}
                  className="block h-[24px] w-[36px] shrink-0 bg-no-repeat"
                  style={{
                    backgroundImage: `url(${paymentMethodsPicture})`,
                    backgroundPosition: `${offset * PAYMENT_SPRITE_SCALE}px -13.2px`,
                    backgroundSize: '518.4px 50.4px',
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <p className="w-full px-[160px] pb-[40px] pt-[12px] text-center font-br text-[14px] leading-[17px] text-white">
          &copy; {new Date().getFullYear()} {t('rightsReserved')}
        </p>
      </div>
    </footer>
  );
}
